import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class ManagementViewportScrollService {
  constructor(@Inject(DOCUMENT) private document: Document) {}

  setManualHistoryScrollRestoration() {
    if (this.document.defaultView?.history.scrollRestoration) {
      this.document.defaultView.history.scrollRestoration = 'manual';
    }
  }

  isReloadNavigation(): boolean {
    const navEntries = this.document.defaultView?.performance?.getEntriesByType('navigation');
    if (navEntries?.length) {
      return (navEntries[0] as PerformanceNavigationTiming).type === 'reload';
    }

    const navigation = (this.document.defaultView?.performance as any)?.navigation;
    return navigation?.type === 1;
  }

  lockPageAt(scrollY: number) {
    this.document.body.style.position = 'fixed';
    this.document.body.style.left = '0';
    this.document.body.style.right = '0';
    this.document.body.style.width = '100%';
    this.document.body.style.top = `-${scrollY}px`;
  }

  releasePageLock() {
    this.document.body.style.removeProperty('position');
    this.document.body.style.removeProperty('left');
    this.document.body.style.removeProperty('right');
    this.document.body.style.removeProperty('width');
    this.document.body.style.removeProperty('top');
  }

  updateModalBackgroundLock(scrollY: number) {
    this.document.body.style.setProperty('--groups-modal-scroll-top', `-${scrollY}px`);
  }

  lockModalBackground(
    scrollY: number,
    preventPointerScroll: (event: Event) => void,
    preventKeyboardScroll: (event: KeyboardEvent) => void
  ) {
    this.updateModalBackgroundLock(scrollY);
    this.document.documentElement.classList.add('groups-modal-background-locked');
    this.document.body.classList.add('groups-modal-background-locked');
    this.document.addEventListener('wheel', preventPointerScroll, { capture: true, passive: false });
    this.document.addEventListener('touchmove', preventPointerScroll, { capture: true, passive: false });
    this.document.addEventListener('keydown', preventKeyboardScroll, true);
  }

  unlockModalBackground(
    scrollY: number,
    preventPointerScroll: (event: Event) => void,
    preventKeyboardScroll: (event: KeyboardEvent) => void
  ) {
    this.document.documentElement.classList.remove('groups-modal-background-locked');
    this.document.body.classList.remove('groups-modal-background-locked');
    this.document.body.style.removeProperty('--groups-modal-scroll-top');
    this.document.defaultView?.scrollTo(0, scrollY);
    this.document.removeEventListener('wheel', preventPointerScroll, true);
    this.document.removeEventListener('touchmove', preventPointerScroll, true);
    this.document.removeEventListener('keydown', preventKeyboardScroll, true);
  }

  isTopDrawerBodyTarget(target: EventTarget | null, isBlocked = false): boolean {
    if (!(target instanceof Element) || isBlocked) {
      return false;
    }

    const drawerBody = target.closest('.edit-drawer-body');
    if (!drawerBody) {
      return false;
    }

    return this.isDrawerBodyInTopActiveDrawer(drawerBody);
  }

  isDrawerScrollEvent(event: Event, isBlocked = false): boolean {
    if (isBlocked) {
      return false;
    }

    for (const node of event.composedPath()) {
      if (!(node instanceof Element)) {
        continue;
      }

      if (!node.classList.contains('edit-drawer-body')) {
        continue;
      }

      if (this.isDrawerBodyInTopActiveDrawer(node)) {
        return true;
      }
    }

    return false;
  }

  private isDrawerBodyInTopActiveDrawer(drawerBody: Element): boolean {
    const activeDrawers = this.document.querySelectorAll('.edit-group-sidebar.p-sidebar-active');
    const topDrawer = activeDrawers.item(activeDrawers.length - 1);

    if (topDrawer?.contains(drawerBody)) {
      return true;
    }

    return !!drawerBody.closest('.edit-group-sidebar');
  }
}
